/* Poster-first, user-controlled movement gallery. No dependency on initLib. */
(() => {
  if (customElements.get('idg-library-gallery')) return;
  customElements.define('idg-library-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('.idg-gallery-track');
      if (!this.track) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.cards = [...this.querySelectorAll('.idg-gallery-card')];
      this.filters = [...this.querySelectorAll('[data-gallery-filter]')];
      this.status = this.querySelector('.idg-gallery-status');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.addEventListener('click', event => {
        const filter = event.target.closest('[data-gallery-filter]');
        const shift = event.target.closest('[data-gallery-shift]');
        const play = event.target.closest('.idg-gallery-play');
        if (filter) this.filter(filter);
        else if (shift && shift.getAttribute('aria-disabled') !== 'true') this.shift(Number(shift.dataset.galleryShift));
        else if (play) this.toggle(play.closest('.idg-gallery-card'));
      }, options);
      this.track.addEventListener('scroll', () => this.scheduleUpdate(), { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') this.shift(event.key === 'ArrowLeft' ? -1 : 1);
        else this.track.scrollTo({ left: event.key === 'Home' ? 0 : this.track.scrollWidth, behavior: this.motion.matches ? 'auto' : 'smooth' });
      }, options);
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.stop(); }, options);
      this.motion.addEventListener('change', () => { if (this.motion.matches) this.stop(); }, options);
      this.cards.forEach(card => {
        const video = card.querySelector('video');
        video.addEventListener('playing', () => {
          if (this.active !== video) { video.pause(); return; }
          card.classList.add('has-frame');
          this.buttonState(video, true);
        }, options);
        video.addEventListener('pause', () => this.buttonState(video, false), options);
        video.addEventListener('error', () => {
          if (!video.getAttribute('src')) return;
          this.stop(video);
          card.classList.remove('has-frame');
          this.status.textContent = 'This preview could not load. Please try again.';
        }, options);
      });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => { if (!entry.isIntersecting || entry.intersectionRatio < .15) this.stop(entry.target.querySelector('video')); });
        }, { threshold: [0, .15] });
        this.cards.forEach(card => this.observer.observe(card));
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
        this.resizeObserver.observe(this.track);
      } else window.addEventListener('resize', () => this.scheduleUpdate(), options);
      this.classList.add('is-ready');
      this.update();
    }

    filter(button) {
      this.stop();
      cancelAnimationFrame(this.filterFrame);
      this.filters.forEach(filter => filter.setAttribute('aria-pressed', String(filter === button)));
      const category = button.dataset.galleryFilter;
      this.cards.forEach(card => {
        card.hidden = category === 'new' ? card.dataset.galleryNew !== 'true' : category !== 'all' && card.dataset.galleryCategory !== category;
      });
      this.track.scrollTo({ left: 0, behavior: 'instant' });
      this.update();
      // A smooth scroll can leave a compositor offset after the grid re-snaps.
      // Reset once after the filtered layout has settled, before the next paint.
      this.filterFrame = requestAnimationFrame(() => {
        this.filterFrame = null;
        this.track.scrollTo({ left: 0, behavior: 'instant' });
        this.update();
      });
      const count = this.cards.filter(card => !card.hidden).length;
      this.status.textContent = `${button.dataset.filterLabel || button.textContent.trim()}: ${count} movement previews.`;
    }

    shift(direction) {
      const card = this.cards.find(item => !item.hidden);
      if (!card) return;
      const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap);
      this.track.scrollBy({ left: direction * step, behavior: this.motion.matches ? 'auto' : 'smooth' });
    }

    scheduleUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    }

    update() {
      const cards = this.cards.filter(card => !card.hidden);
      const viewport = this.track.getBoundingClientRect();
      const visible = cards.map((card, index) => ({ rect: card.getBoundingClientRect(), index }))
        .filter(item => item.rect.right > viewport.left + 2 && item.rect.left < viewport.right - 2);
      const first = visible.length ? visible[0].index + 1 : 0;
      const last = visible.length ? visible[visible.length - 1].index + 1 : 0;
      this.querySelector('.idg-gallery-position').textContent = `${String(first).padStart(2, '0')}–${String(last).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`;
      this.querySelector('[data-gallery-shift="-1"]').setAttribute('aria-disabled', String(this.track.scrollLeft <= 2));
      this.querySelector('[data-gallery-shift="1"]').setAttribute('aria-disabled', String(this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2));
      if (this.active) {
        const r = this.active.getBoundingClientRect();
        if (r.right <= viewport.left || r.left >= viewport.right) this.stop();
      }
    }

    buttonState(video, playing) {
      const button = video.closest('.idg-gallery-card').querySelector('.idg-gallery-play');
      button.setAttribute('aria-pressed', String(playing));
      button.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${button.dataset.movementName}`);
    }

    stop(video = this.active) {
      if (!video) return;
      if (this.active === video) this.active = null;
      video.pause();
      this.buttonState(video, false);
    }

    async toggle(card) {
      const video = card.querySelector('video');
      if (this.active === video) { this.stop(); return; }
      this.stop();
      this.active = video;
      this.status.textContent = '';
      this.buttonState(video, true);
      if (!video.getAttribute('src') || video.error) {
        video.src = video.dataset.src;
        video.load();
      }
      try {
        await video.play();
        if (this.active !== video) video.pause();
      } catch (error) {
        if (this.active !== video) return;
        this.stop(video);
        if (error.name !== 'AbortError') this.status.textContent = 'This preview could not play. Please try again.';
      }
    }

    disconnectedCallback() {
      this.stop();
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.filterFrame);
      this.frame = null;
      this.filterFrame = null;
      this.classList.remove('is-ready');
    }
  });
})();
